import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';
import { getInstalledBase } from '../../core/systems/MarketSystem';

const LAST_PROJECT_KEY = 'officetycoon_last_project';

function getLastProject(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(LAST_PROJECT_KEY) ?? '{}'); } catch { return {}; }
}

function saveLastProject(vals: Record<string, string>): void {
  localStorage.setItem(LAST_PROJECT_KEY, JSON.stringify(vals));
}

function createSelect(options: { value: string; label: string; disabled?: boolean }[], defaultValue?: string): HTMLSelectElement {
  const select = document.createElement('select');
  select.style.cssText = 'padding:6px 8px;background:var(--color-bg-primary);border:1px solid var(--border-color);border-radius:4px;color:var(--color-text-primary);font-family:var(--font-ui);font-size:13px;width:100%;';
  for (const opt of options) {
    const el = document.createElement('option');
    el.value = opt.value;
    el.textContent = opt.label;
    if (opt.disabled) el.disabled = true;
    if (defaultValue === opt.value) el.selected = true;
    select.appendChild(el);
  }
  return select;
}

function createRow(label: string, input: HTMLElement): HTMLDivElement {
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;flex-direction:column;gap:4px;';
  const lbl = document.createElement('label');
  lbl.textContent = label;
  lbl.style.cssText = 'font-size:11px;color:var(--color-text-secondary);';
  row.appendChild(lbl);
  row.appendChild(input);
  return row;
}

/**
 * Dialog for starting a new project.
 * Category, subcategory, platform, size, marketing, team, name.
 */
export function openStartProjectDialog(parent: HTMLElement, store: GameStore, onClose: () => void): void {
  const state = store.getState();
  const theme = store.getThemeData();
  const prevSpeed = state.speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  // Find available teams (have members, no active project)
  const availableTeams = state.teams.filter(
    (t) => t.memberIds.length > 0 && !t.assignedProjectId,
  );

  if (availableTeams.length === 0) {
    // No teams available — show error dialog
    const dialog = new Dialog({
      title: 'Start Project',
      onClose: () => {
        store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
        onClose();
      },
      actions: [{ label: 'OK', style: 'primary', onClick: () => dialog.close() }],
    });
    const msg = document.createElement('p');
    msg.textContent = state.teams.length === 0
      ? 'You need to create a team first.'
      : 'All teams are busy with projects.';
    msg.style.cssText = 'color:var(--color-text-secondary);';
    dialog.body.appendChild(msg);
    dialog.mount(parent);
    return;
  }

  // Available platforms (released by current year)
  const platforms = theme.entities.platforms.filter(
    (p) => p.releasedYear <= state.company.currentYear && (!p.retiredYear || p.retiredYear > state.company.currentYear),
  );

  // Marketing strategies — always include "none", plus affordable ones
  const strategies = theme.entities.marketingStrategies.filter(
    (s) => s.id === 'none' || (s.cost <= state.company.funds && s.minCompanyLevel <= state.company.level),
  );

  const errorEl: HTMLDivElement = document.createElement('div');
  const last = getLastProject();

  const categorySelect = createSelect(theme.entities.projectCategories.map((c) => ({ value: c.id, label: c.name })), last.category);
  const subcategorySelect = createSelect(theme.entities.projectSubcategories.map((s) => ({ value: s.id, label: s.name })), last.subcategory);
  // Multi-platform checkboxes
  const platformContainer = document.createElement('div');
  platformContainer.style.cssText = 'display:flex;flex-direction:column;gap:3px;max-height:120px;overflow-y:auto;';
  const platformChecks: { id: string; checkbox: HTMLInputElement }[] = [];
  const lastPlatforms = last.platforms ? last.platforms.split(',') : [last.platform ?? platforms[0]?.id];

  for (const p of platforms) {
    const row = document.createElement('label');
    row.style.cssText = 'display:flex;align-items:center;gap:6px;font-size:11px;cursor:pointer;padding:2px 0;';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = lastPlatforms.includes(p.id);
    const base = getInstalledBase(p, state.company.currentYear);
    const baseK = Math.round(base / 1000);
    const cost = p.licenseCost > 0 ? ` · $${p.licenseCost}` : '';
    const isNew = state.company.currentYear - p.releasedYear <= 1;
    const tag = isNew ? ' <span style="color:var(--color-success);">NEW</span>' : '';
    row.appendChild(cb);
    const lbl = document.createElement('span');
    lbl.innerHTML = `${p.name} (${baseK}K users${cost})${tag}`;
    row.appendChild(lbl);
    platformContainer.appendChild(row);
    platformChecks.push({ id: p.id, checkbox: cb });
  }
  // Ensure at least one is checked
  if (!platformChecks.some((pc) => pc.checkbox.checked) && platformChecks[0]) {
    platformChecks[0].checkbox.checked = true;
  }
  const sizeSelect = createSelect([
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'aaa', label: 'AAA' },
  ], last.size);
  const marketingSelect = createSelect(strategies.map((s) => ({
    value: s.id,
    label: `${s.name} ($${s.cost})`,
  })), last.marketing);
  const teamSelect = createSelect(availableTeams.map((t) => ({
    value: t.id,
    label: `${t.name} (${t.memberIds.length} members)`,
  })));

  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.placeholder = 'Auto-generated if blank';
  nameInput.maxLength = 30;
  nameInput.style.cssText = 'padding:6px 8px;background:var(--color-bg-primary);border:1px solid var(--border-color);border-radius:4px;color:var(--color-text-primary);font-family:var(--font-ui);font-size:13px;width:100%;';

  const dialog = new Dialog({
    title: 'Start New Project',
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
    actions: [
      {
        label: 'Start Project',
        style: 'primary',
        onClick: () => {
          const selectedPlatforms = platformChecks.filter((pc) => pc.checkbox.checked).map((pc) => pc.id);
          if (selectedPlatforms.length === 0) {
            errorEl.textContent = 'Select at least one platform.';
            return;
          }
          const result = store.dispatch({
            type: 'START_PROJECT',
            params: {
              category: categorySelect.value,
              subcategory: subcategorySelect.value,
              platform: selectedPlatforms[0]!,
              platforms: selectedPlatforms,
              size: sizeSelect.value as 'small' | 'medium' | 'large' | 'aaa',
              name: nameInput.value.trim(),
              teamId: teamSelect.value,
              marketingStrategyId: marketingSelect.value,
              isSequel: !!detectedSequel,
              sequelOf: detectedSequel?.id,
            },
          });
          if (!result.success) {
            errorEl.textContent = result.error ?? 'Failed to start project.';
            return;
          }
          saveLastProject({
            category: categorySelect.value,
            subcategory: subcategorySelect.value,
            platforms: selectedPlatforms.join(','),
            platform: selectedPlatforms[0]!,
            size: sizeSelect.value,
            marketing: marketingSelect.value,
          });
          dialog.close();
        },
      },
    ],
  });

  const form = document.createElement('div');
  form.style.cssText = 'display:flex;flex-direction:column;gap:10px;min-width:280px;';

  // Market trend hint — only show if player has discovered this combo before
  const trendEl = document.createElement('div');
  trendEl.style.cssText = 'font-size:11px;min-height:16px;text-align:center;';

  const updateTrend = () => {
    const cat = categorySelect.value;
    const sub = subcategorySelect.value;
    // Only show trend if player has released a game with this combo
    const hasReleased = state.products.some((p) => p.category === cat && p.subcategory === sub);
    if (!hasReleased) {
      trendEl.textContent = '';
      return;
    }
    // Show market trend from state (dynamic, not static theme data)
    const key = `${cat}+${sub}`;
    const trend = state.marketTrends?.[key] ?? 1.0;
    if (trend > 1.1) {
      trendEl.textContent = `📈 Hot trend! Reviewers love this combo right now.`;
      trendEl.style.color = 'var(--color-success)';
    } else if (trend < 0.9) {
      trendEl.textContent = `📉 Oversaturated. Reviewers are tired of this combo.`;
      trendEl.style.color = 'var(--color-warning)';
    } else {
      trendEl.textContent = `📊 Neutral market for this combo.`;
      trendEl.style.color = 'var(--color-text-muted)';
    }
  };
  // Sequel detection
  const sequelEl = document.createElement('div');
  sequelEl.style.cssText = 'font-size:11px;min-height:16px;text-align:center;';
  let detectedSequel: { id: string; name: string; number: number } | null = null;

  const updateSequel = () => {
    const cat = categorySelect.value;
    const sub = subcategorySelect.value;
    const plat = platformChecks.find((pc) => pc.checkbox.checked)?.id ?? '';
    // Find the most recent matching released product
    const match = [...state.products].reverse().find(
      (p) => p.category === cat && p.subcategory === sub && p.platform === plat,
    );
    if (match) {
      // Count existing sequels: released products + in-progress projects with same combo
      const releasedCount = state.products.filter(
        (p) => p.category === cat && p.subcategory === sub && p.platform === plat,
      ).length;
      const inProgressCount = state.projects.filter(
        (p) => p.category === cat && p.subcategory === sub && p.platform === plat && p.phase !== 'complete',
      ).length;
      const nextNum = releasedCount + inProgressCount + 1;
      const baseName = match.name.replace(/ \d+$/, '');
      const suggestedName = `${baseName} ${nextNum}`;
      detectedSequel = { id: match.id, name: suggestedName, number: nextNum - 1 };
      sequelEl.innerHTML = `🔄 Sequel to "${match.name}" · <span style="color:var(--color-success);">+10% quality bonus</span>`;
      sequelEl.style.color = 'var(--color-info)';
      if (!nameInput.value) nameInput.value = suggestedName;
    } else {
      detectedSequel = null;
      sequelEl.textContent = '';
    }
  };

  categorySelect.addEventListener('change', () => { updateTrend(); updateSequel(); });
  subcategorySelect.addEventListener('change', () => { updateTrend(); updateSequel(); });
  for (const pc of platformChecks) pc.checkbox.addEventListener('change', updateSequel);
  updateTrend();
  updateSequel();

  form.appendChild(createRow('Genre', categorySelect));
  form.appendChild(createRow('Theme', subcategorySelect));
  form.appendChild(trendEl);
  form.appendChild(createRow('Platforms', platformContainer));
  form.appendChild(sequelEl);
  form.appendChild(createRow('Size', sizeSelect));
  form.appendChild(createRow('Marketing', marketingSelect));
  form.appendChild(createRow('Team', teamSelect));
  form.appendChild(createRow('Project Name', nameInput));

  errorEl.style.cssText = 'color:var(--color-danger);font-size:12px;min-height:16px;';
  form.appendChild(errorEl);

  dialog.body.appendChild(form);
  dialog.mount(parent);
}
